<script setup lang="ts">
import { computed, PropType } from 'vue';

import { CheckCircleIcon, InformationCircleIcon, ShieldExclamationIcon, XCircleIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/24/solid'

import { NotificationProps } from '@/interfaces';
import NotificationAction from './NotificationAction.vue';
import { OctagonAlert } from 'lucide-vue-next';

const props = defineProps({
  remainingDuration: {
    type: Number,
    required: true,
  },
  data: {
    type: Object as PropType<NotificationProps>,
    required: true,
  }
});
</script>

<template>
  <!-- Classic notification -->
  <div v-if="!('cta' in data)">
    <div class="w-full max-w-sm mb-4 overflow-hidden bg-black border-gray-800 rounded-lg shadow-lg pointer-events-auto bg-opacity-80 backdrop-blur backdrop-filter hover:backdrop-blur-lg border-y-2 ring-1 ring-black ring-opacity-5">
      <div class="p-4">
        <div class="flex items-start text-left">
          <div class="flex-shrink-0">
            <svg v-if="data.type === 'wait'" class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <CheckCircleIcon v-if="!data.type || data.type === 'success'" class="w-6 h-6 text-green-400" aria-hidden="true" />
            <XCircleIcon v-if="data.type === 'error'" class="text-red-600 w-7 h-7" aria-hidden="true" />
            <OctagonAlert v-if="data.type === 'warning'" class="w-6 h-6 text-amber-500" aria-hidden="true" />
            <InformationCircleIcon v-if="data.type === 'info'" class="w-6 h-6 text-blue-500" aria-hidden="true" />
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-semibold text-white">{{ props.data.title }}</p>
            <p v-if="data.message" class="mt-1 text-sm font-medium text-white ">{{ props.data.message }}</p>
          </div>
          
          <div class="flex flex-shrink-0 ml-4">
            <button v-if="data.type === 'cancellableRequest'" type="button"
              @click="$emit('cancel-request')"
              class="flex-shrink-0 mr-3 text-sm font-bold text-blue-100 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500">
              Annuler ({{ Math.ceil(remainingDuration/1000) || 1 }} sec)
            </button>
            <button @click="$emit('close')" class="inline-flex text-gray-400 rounded-md hover:text-gray-300 focus:text-red-400">
              <span class="sr-only">Close</span>
              <XMarkIcon class="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Action notification -->
  <div v-else>
    <NotificationAction
      @close="$emit('close')"
      :remaining-duration="remainingDuration"
      :data="data"
    />
  </div>
</template>
